import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"];
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { CardGrid } from 'foundations-components/transitional/components/CardGrid';
import { Image } from 'foundations-components/transitional/components/Image';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { MarketplaceCardMedium } from 'marketplace-embed/components/MarketplaceCardMedium';
import { useMarketplaceListingModal } from 'marketplace-embed/hooks/useMarketplaceListingModal';
import { useUsageTracker } from 'marketplace-ui-tracking';
import { useState } from 'react';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { useGetStorefrontListings } from '../hooks/useGetStorefrontListings';
import { openListingModalOrListingInNewPage } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_AUTOMATION_ACTIONS = 3;
const MAX_AUTOMATION_LISTINGS = 4;
const ICON_SIZE = '32px';
function AutomationActionCard(_ref) {
  let {
      children
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(Flex, Object.assign({
    borderRadius: "300",
    borderColor: "border-core-subtle",
    borderStyle: "solid",
    borderWidth: "100",
    paddingInline: "400",
    paddingBlock: "300"
  }, otherProps, {
    children: children
  }));
}
export function AutomationsTab({
  automationActions = [],
  automationListings = []
}) {
  const {
    appContext,
    name,
    iconUrl,
    isEmbedded
  } = useDetailsPageContext();
  const {
    open: openListingDetailsModal
  } = useMarketplaceListingModal({});
  const tracker = useUsageTracker();
  const [expandedActions, setExpandedActions] = useState(false);
  const [expandedListings, setExpandedListings] = useState(false);
  const {
    listingCards,
    loading: automationsListingDataLoading
  } = useGetStorefrontListings({
    searchParams: {
      searchIds: automationListings.map(id => parseInt(id, 10)) || [],
      searchBy: 'LISTING_ID'
    },
    skip: appContext === 'admin-preview'
  });
  const automationActionsToShow = expandedActions ? automationActions : automationActions.slice(0, MAX_AUTOMATION_ACTIONS);
  const listingCardsToShow = expandedListings ? listingCards : listingCards === null || listingCards === void 0 ? void 0 : listingCards.slice(0, MAX_AUTOMATION_LISTINGS);
  const isAutomationListingClickable = appContext === 'app';
  function handleAutomationListingClicked(slug) {
    if (!isAutomationListingClickable) {
      return null; //should not end up here but adding just in case
    }
    openListingModalOrListingInNewPage(slug, !!isEmbedded, openListingDetailsModal);
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "600",
    marginBlockStart: "400",
    children: [/*#__PURE__*/_jsx(Text, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.functionalityCard.tabs.automations.description"
      })
    }), automationActions.length > 0 && /*#__PURE__*/_jsxs(Flex, {
      gap: "300",
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.functionalityCard.tabs.automations.workflowActions",
          options: {
            name
          }
        })
      }), automationActionsToShow.map(action => /*#__PURE__*/_jsxs(AutomationActionCard, {
        flexDirection: "row",
        gap: "500",
        children: [/*#__PURE__*/_jsx(Image, {
          blockSize: ICON_SIZE,
          maxBlockSize: ICON_SIZE,
          inlineSize: ICON_SIZE,
          maxInlineSize: ICON_SIZE,
          objectFit: 'cover',
          src: iconUrl,
          alt: `${name}-icon`
        }), /*#__PURE__*/_jsx(View, {
          alignSelf: "center",
          children: /*#__PURE__*/_jsx(Text, {
            as: "h6",
            children: action.name
          })
        })]
      }, action.name)), automationActions.length > MAX_AUTOMATION_ACTIONS && /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        onClick: () => setExpandedActions(!expandedActions),
        children: expandedActions ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.functionalityCard.tabs.automations.showLess"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.functionalityCard.tabs.automations.viewAll"
        })
      })]
    }), automationListings.length > 0 && /*#__PURE__*/_jsxs(View, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        marginBlockEnd: "300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.functionalityCard.tabs.automations.workflowTemplates",
          options: {
            name
          }
        })
      }), /*#__PURE__*/_jsx(CardGrid, {
        gap: "400",
        columns: 2,
        marginBlockEnd: "200",
        children: automationsListingDataLoading ? automationListings.map(listing => /*#__PURE__*/_jsx(CardWrapper, {
          compact: true,
          children: /*#__PURE__*/_jsx(View, {
            alignSelf: "center",
            children: /*#__PURE__*/_jsx(LoadingSpinner, {
              use: "primary-white"
            })
          })
        }, listing)) : listingCardsToShow.map(listingCard => /*#__PURE__*/_jsx(MarketplaceCardMedium, {
          tracker: tracker,
          listing: listingCard,
          onClick: isAutomationListingClickable ? () => handleAutomationListingClicked(listingCard.slug) : undefined
        }, listingCard.listingId))
      }), (listingCards === null || listingCards === void 0 ? void 0 : listingCards.length) > MAX_AUTOMATION_LISTINGS && /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        onClick: () => setExpandedListings(!expandedListings),
        children: expandedListings ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.functionalityCard.tabs.automations.showLess"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.functionalityCard.tabs.automations.viewAll"
        })
      })]
    })]
  });
}